import { useState } from "react";
import { motion } from "framer-motion";
import { PhoneCall, MessageSquare, WashingMachine, Refrigerator, Flame, Droplets, ShieldCheck, MapPin, Clock, CheckCircle2, Phone, MessageCircle, CreditCard, Banknote, Wallet } from "lucide-react";
import "@/App.css";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import ContactForm from "@/components/ContactForm";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const PHONE_NUMBER = "+18135083663";
const PHONE_DISPLAY = "(813) 508-3663";
const EMAIL = "contact@pascoappliance.com";

// Sticky Header Component
const Header = () => {
  const [serviceAreasOpen, setServiceAreasOpen] = useState(false);
  
  const serviceAreas = [
    "Pasco",
    "North Hillsborough",
    "North Pinellas"
  ];

  return (
    <motion.header 
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.3 }}
      className="sticky top-0 z-50 bg-white/85 backdrop-blur supports-[backdrop-filter]:bg-white/70 border-b border-slate-200"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">
              <img 
                src="/images/logo.jpg"
                alt="Pasco Appliance LLC Logo"
                className="h-14 w-14 rounded-full object-contain bg-white"
              />
            </div>
            <div>
              <h1 className="text-base font-semibold text-[color:var(--navy)]">Pasco Appliance LLC</h1>
              <p className="text-xs text-slate-600">Licensed & Insured</p>
            </div>
          </div>

          {/* Service Area Chip & Click-to-Call */}
          <div className="flex items-center gap-3">
            <Popover open={serviceAreasOpen} onOpenChange={setServiceAreasOpen}>
              <PopoverTrigger asChild>
                <button
                  data-testid="header-service-area-chip"
                  onMouseEnter={() => setServiceAreasOpen(true)}
                  onMouseLeave={() => setServiceAreasOpen(false)}
                  className="hidden sm:inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-700 hover:bg-[color:var(--steel)] hover:border-[color:var(--navy)]/20 transition-all cursor-pointer"
                >
                  <MapPin className="mr-1 h-3 w-3" />
                  Serving Odessa (33556)
                </button>
              </PopoverTrigger>
              <PopoverContent 
                className="w-56 p-3"
                onMouseEnter={() => setServiceAreasOpen(true)}
                onMouseLeave={() => setServiceAreasOpen(false)}
              >
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-[color:var(--navy)]">Service Areas</h4>
                  <ul className="space-y-1.5">
                    {serviceAreas.map((area, index) => (
                      <li key={index} className="flex items-center gap-2 text-sm text-slate-700">
                        <CheckCircle2 className="h-3.5 w-3.5 text-[color:var(--chat-green)]" />
                        {area}
                      </li>
                    ))}
                  </ul>
                </div>
              </PopoverContent>
            </Popover>
            
            <div className="flex flex-col items-end gap-0.5">
              <a 
                href={`tel:${PHONE_NUMBER}`}
                data-testid="header-click-to-call-link"
                className="inline-flex items-center gap-2 text-sm font-semibold text-[color:var(--navy)] hover:text-[color:var(--navy-2)] transition-colors"
              >
                <PhoneCall className="h-4 w-4" />
                <span className="hidden sm:inline">{PHONE_DISPLAY}</span>
                <span className="sm:hidden">Call</span>
              </a>
              <span className="hidden lg:block text-[10px] text-slate-500 italic">
                English | Español | Русский
              </span>
            </div>
          </div>
        </div>
      </div>
    </motion.header>
  );
};

// Hero Section Component
const Hero = () => {
  const openVoiceflowChat = () => {
    if (window.voiceflow && window.voiceflow.chat) {
      window.voiceflow.chat.open();
    }
  };

  return (
    <section className="py-10 sm:py-14 noise">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 items-center">
          {/* Left Column - Content */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="md:col-span-7"
          >
            <h2 
              data-testid="hero-headline"
              className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.02em] text-[color:var(--navy)] leading-tight"
            >
              Fast, Honest Appliance Repair in Pasco County
            </h2>
            
            <p 
              data-testid="hero-subheadline"
              className="mt-4 text-base md:text-lg text-slate-700 leading-relaxed"
            >
              Same-day service when available. Upfront pricing. Real local help—no corporate runaround.
            </p>

            {/* CTA Row */}
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <motion.div whileHover={{ y: -1 }} whileTap={{ scale: 0.98 }}>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      data-testid="hero-primary-cta-button"
                      size="lg"
                      className="w-full sm:w-auto min-h-11 bg-[color:var(--safety-orange)] text-white hover:bg-[#ff4a12] focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[color:var(--safety-orange-2)] shadow-[0_4px_14px_rgba(255,90,31,0.25)] transition-all"
                    >
                      <PhoneCall className="h-5 w-5 mr-2" />
                      Text / Call for a Quote
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent 
                    align="start" 
                    className="w-56"
                    sideOffset={5}
                  >
                    <DropdownMenuItem asChild>
                      <a 
                        href={`tel:${PHONE_NUMBER}`}
                        className="flex items-center gap-2 cursor-pointer px-3 py-2"
                      >
                        <Phone className="h-4 w-4 text-[color:var(--navy)]" />
                        <div className="flex flex-col">
                          <span className="font-medium text-sm">Call Now</span>
                          <span className="text-xs text-slate-500">{PHONE_DISPLAY}</span>
                        </div>
                      </a>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <a 
                        href={`sms:${PHONE_NUMBER}`}
                        className="flex items-center gap-2 cursor-pointer px-3 py-2"
                      >
                        <MessageCircle className="h-4 w-4 text-[color:var(--navy)]" />
                        <div className="flex flex-col">
                          <span className="font-medium text-sm">Send SMS</span>
                          <span className="text-xs text-slate-500">Text us anytime</span>
                        </div>
                      </a>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </motion.div>

              <motion.div whileHover={{ y: -1 }} whileTap={{ scale: 0.98 }}>
                <div className="inline-block">
                  <Button 
                    onClick={openVoiceflowChat}
                    data-testid="hero-secondary-chat-button"
                    variant="outline"
                    size="lg"
                    className="w-full sm:w-auto min-h-11 border-[color:var(--navy)] text-[color:var(--navy)] hover:bg-[color:var(--steel)] transition-all"
                  >
                    <MessageSquare className="mr-2 h-5 w-5" />
                    Chat with our Assistant
                  </Button>
                  <p className="text-xs text-slate-500 italic mt-1 text-center">
                    Начните на русском / Empiece en español
                  </p>
                </div>
              </motion.div>
            </div>

            {/* Proof Chips */}
            <div className="mt-6 flex flex-wrap gap-2">
              <div 
                data-testid="hero-proof-licensed"
                className="inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-200 px-3 py-1 text-xs text-slate-700"
              >
                <ShieldCheck className="h-3.5 w-3.5 text-[color:var(--navy)]" />
                Licensed & insured
              </div>
              <div 
                data-testid="hero-proof-sameday"
                className="inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-200 px-3 py-1 text-xs text-slate-700"
              >
                <Clock className="h-3.5 w-3.5 text-[color:var(--navy)]" />
                Same-day when available
              </div>
              <div 
                data-testid="hero-proof-area"
                className="inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-200 px-3 py-1 text-xs text-slate-700"
              >
                <MapPin className="h-3.5 w-3.5 text-[color:var(--navy)]" />
                Serving 33556 + Pasco
              </div>
            </div>

            {/* Language Support */}
            <div className="mt-4 inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gradient-to-r from-[color:var(--steel)] to-white border border-slate-200">
              <span className="text-xs font-medium text-[color:var(--navy)]">
                English | Se Habla Español | Мы говорим по-русски
              </span>
            </div>
          </motion.div>

          {/* Right Column - Hero Image */}
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="md:col-span-5"
          >
            <Card className="rounded-2xl border border-slate-200 bg-white shadow-[0_10px_30px_rgba(2,6,23,0.08)] overflow-hidden">
              <AspectRatio ratio={4/3}>
                <img 
                  data-testid="hero-image"
                  src="https://images.unsplash.com/photo-1571815523256-909eccd75b4c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwxfHxhcHBsaWFuY2UlMjByZXBhaXIlMjB0ZWNobmljaWFuJTIwaGFuZHMlMjB0b29scyUyMGNsb3NlJTIwdXB8ZW58MHx8fGJsdWV8MTc3NjA3ODc0NXww&ixlib=rb-4.1.0&q=85"
                  alt="Professional technician tools close-up"
                  className="object-cover w-full h-full"
                />
              </AspectRatio>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Services Grid Component
const Services = () => {
  const services = [
    { icon: WashingMachine, title: "Washers & Dryers", testId: "service-card-washers-dryers" },
    { icon: Refrigerator, title: "Refrigerators", testId: "service-card-refrigerators" },
    { icon: Flame, title: "Ovens & Stoves", testId: "service-card-ovens-stoves" },
    { icon: Droplets, title: "Dishwashers", testId: "service-card-dishwashers" },
  ];

  return (
    <section className="py-14 sm:py-18 bg-[#F6F9FC]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10">
          <h3 className="text-3xl sm:text-4xl font-semibold text-[color:var(--navy)] tracking-[-0.02em]">
            What We Fix
          </h3>
          <p className="mt-3 text-base text-slate-600">
            Expert repair for all major appliance brands
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <motion.div
                key={service.testId}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <Card 
                  data-testid={service.testId}
                  className="rounded-2xl border border-slate-200 bg-white p-5 hover:shadow-[0_14px_40px_rgba(2,6,23,0.10)] hover:-translate-y-0.5 transition-all duration-200"
                >
                  <CardContent className="p-0">
                    <motion.div 
                      whileHover={{ rotate: 2 }}
                      className="inline-flex items-center justify-center h-12 w-12 rounded-lg bg-[color:var(--steel)] text-[color:var(--navy)] mb-3"
                    >
                      <Icon className="h-6 w-6" strokeWidth={1.75} />
                    </motion.div>
                    <h4 className="text-base font-semibold text-[color:var(--navy)]">
                      {service.title}
                    </h4>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* EPA Badge */}
        <div className="mt-6 text-center">
          <Badge 
            data-testid="epa-certified-badge"
            data-font="mono"
            variant="outline"
            className="inline-flex items-center gap-2 rounded-full border border-[color:var(--navy)]/20 bg-[color:var(--steel)] text-[color:var(--navy)] px-3 py-1 text-xs"
          >
            <ShieldCheck className="h-3.5 w-3.5" />
            EPA CERTIFIED
          </Badge>
        </div>
      </div>
    </section>
  );
};

// About Section Component
const About = () => {
  return (
    <section className="py-14 sm:py-18">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
          <div className="md:col-span-8">
            <h3 className="text-3xl sm:text-4xl font-semibold text-[color:var(--navy)] tracking-[-0.02em] mb-6">
              Local. Independent. Honest.
            </h3>
            
            <Card 
              data-testid="about-owner-card"
              className="rounded-2xl border border-slate-200 bg-white p-6"
            >
              <CardContent className="p-0">
                <div className="flex items-start gap-4 mb-4">
                  <div className="flex-shrink-0">
                    <img 
                      src="/images/owner-photo.png"
                      alt="Alex - Owner of Pasco Appliance LLC"
                      className="h-20 w-20 rounded-full object-cover object-center ring-2 ring-[color:var(--steel)]"
                    />
                  </div>
                  <div>
                    <p 
                      data-testid="about-owner-label"
                      data-font="mono"
                      className="text-xs uppercase tracking-widest text-slate-500 mb-1"
                    >
                      OWNER-OPERATOR
                    </p>
                    <p className="text-base font-semibold text-[color:var(--navy)]">
                      Alex, Pasco Appliance LLC
                    </p>
                  </div>
                </div>

                <Separator className="my-4" />

                <div className="text-sm md:text-base text-slate-700 leading-relaxed space-y-3">
                  <p>
                    I'm Alex, a local Odessa resident. I started Pasco Appliance LLC because I was tired of massive repair chains overcharging our community and sending sub-contractors who don't care.
                  </p>
                  <p>
                    When you hire Pasco Appliance, you deal directly with me. I show up on time, diagnose the problem accurately, and give you a flat-rate price before any work begins.
                  </p>
                  <p className="font-medium text-[color:var(--navy)]">
                    Simple as that.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="md:col-span-4">
            <Card className="rounded-2xl border border-slate-200 bg-white shadow-[0_10px_30px_rgba(2,6,23,0.08)] overflow-hidden">
              <AspectRatio ratio={3/4}>
                <img 
                  src="https://images.unsplash.com/photo-1584677191047-38f48d0db64e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHw0fHxhcHBsaWFuY2UlMjByZXBhaXIlMjB0ZWNobmljaWFuJTIwaGFuZHMlMjB0b29scyUyMGNsb3NlJTIwdXB8ZW58MHx8fGJsdWV8MTc3NjA3ODc0NXww&ixlib=rb-4.1.0&q=85"
                  alt="Professional repair tools"
                  className="object-cover w-full h-full"
                  loading="lazy"
                />
              </AspectRatio>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

// How It Works Component
const HowItWorks = () => {
  const steps = [
    {
      number: "1",
      title: "Call or Text",
      description: "Tell us what's going on + your model number if you have it.",
      testId: "how-step-1"
    },
    {
      number: "2",
      title: "Get a Clear Quote",
      description: "Upfront pricing before work starts. No surprises.",
      testId: "how-step-2"
    },
    {
      number: "3",
      title: "We Fix It Fast",
      description: "Same-day when available. Clean work. Respect your home.",
      testId: "how-step-3"
    }
  ];

  return (
    <section className="py-14 sm:py-18 bg-[#F6F9FC]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10">
          <h3 className="text-3xl sm:text-4xl font-semibold text-[color:var(--navy)] tracking-[-0.02em]">
            How It Works
          </h3>
          <p className="mt-3 text-base text-slate-600">
            3 simple steps to getting your appliances fixed
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {steps.map((step, index) => (
            <motion.div
              key={step.testId}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.15 }}
            >
              <Card 
                data-testid={step.testId}
                className="rounded-2xl border border-slate-200 bg-white p-6 h-full"
              >
                <CardContent className="p-0">
                  <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-[color:var(--navy)] text-white font-bold text-xl mb-4">
                    {step.number}
                  </div>
                  <h4 className="text-lg font-semibold text-[color:var(--navy)] mb-2">
                    {step.title}
                  </h4>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {step.description}
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* CTA Below Steps */}
        <div className="mt-8 text-center">
          <motion.div whileHover={{ y: -1 }} whileTap={{ scale: 0.98 }}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  size="lg"
                  className="min-h-11 bg-[color:var(--safety-orange)] text-white hover:bg-[#ff4a12] focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[color:var(--safety-orange-2)] shadow-[0_4px_14px_rgba(255,90,31,0.25)] transition-all"
                >
                  <PhoneCall className="h-5 w-5 mr-2" />
                  Get Started - Call Now
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent 
                align="center" 
                className="w-56"
                sideOffset={5}
              >
                <DropdownMenuItem asChild>
                  <a 
                    href={`tel:${PHONE_NUMBER}`}
                    className="flex items-center gap-2 cursor-pointer px-3 py-2"
                  >
                    <Phone className="h-4 w-4 text-[color:var(--navy)]" />
                    <div className="flex flex-col">
                      <span className="font-medium text-sm">Call Now</span>
                      <span className="text-xs text-slate-500">{PHONE_DISPLAY}</span>
                    </div>
                  </a>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <a 
                    href={`sms:${PHONE_NUMBER}`}
                    className="flex items-center gap-2 cursor-pointer px-3 py-2"
                  >
                    <MessageCircle className="h-4 w-4 text-[color:var(--navy)]" />
                    <div className="flex flex-col">
                      <span className="font-medium text-sm">Send SMS</span>
                      <span className="text-xs text-slate-500">Text us anytime</span>
                    </div>
                  </a>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Payment Methods Component
const PaymentMethods = () => {
  const paymentMethods = [
    { name: "Visa", icon: CreditCard },
    { name: "Mastercard", icon: CreditCard },
    { name: "Amex", icon: CreditCard },
    { name: "Cash", icon: Banknote },
    { name: "Venmo", icon: Wallet },
    { name: "PayPal", icon: Wallet },
    { name: "Zelle", icon: Wallet },
    { name: "CashApp", icon: Wallet },
  ];

  return (
    <section className="py-14 sm:py-16 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-8">
          <h3 className="text-2xl sm:text-3xl font-semibold text-[color:var(--navy)] tracking-[-0.02em] mb-2">
            Flexible Payment Options
          </h3>
          <p className="text-base text-slate-600">
            We accept multiple payment methods for your convenience
          </p>
        </div>

        <div className="flex flex-wrap justify-center items-center gap-3 sm:gap-4">
          {paymentMethods.map((method, index) => {
            const Icon = method.icon;
            return (
              <motion.div
                key={method.name}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
              >
                <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border-2 border-slate-200 bg-white hover:border-[color:var(--navy)]/20 hover:shadow-sm transition-all">
                  <Icon className="h-5 w-5 text-[color:var(--navy)]" strokeWidth={1.75} />
                  <span className="text-sm font-medium text-[color:var(--navy)]">
                    {method.name}
                  </span>
                </div>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-6 text-center">
          <p className="text-sm text-slate-500 italic">
            Payment due upon completion of service
          </p>
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer className="bg-[color:var(--navy)] text-white py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Logo Section */}
        <div className="flex justify-center mb-8">
          <img 
            src="/images/logo.jpg"
            alt="Pasco Appliance LLC - Fast, Honest Appliance Repair"
            className="h-32 w-auto"
          />
        </div>

        <Separator className="my-8 bg-white/10" />

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Phone */}
          <div data-testid="footer-phone">
            <h5 className="text-sm font-semibold uppercase tracking-wider mb-3">
              Contact
            </h5>
            <a 
              href={`tel:${PHONE_NUMBER}`}
              data-testid="footer-click-to-call-link"
              className="text-lg font-bold underline decoration-white/30 underline-offset-4 hover:decoration-white transition-all"
            >
              {PHONE_DISPLAY}
            </a>
            <p className="mt-2 text-sm text-slate-300">
              Call or text anytime
            </p>
            <p className="mt-3 text-xs text-slate-400">
              English<br />
              Se Habla Español<br />
              Мы говорим по-русски
            </p>
          </div>

          {/* Service Areas */}
          <div data-testid="footer-service-areas">
            <h5 className="text-sm font-semibold uppercase tracking-wider mb-3">
              Service Areas
            </h5>
            <ul className="space-y-1 text-sm text-slate-300">
              <li className="flex items-center gap-1">
                <CheckCircle2 className="h-3.5 w-3.5 text-[color:var(--chat-green)]" />
                Pasco
              </li>
              <li className="flex items-center gap-1">
                <CheckCircle2 className="h-3.5 w-3.5 text-[color:var(--chat-green)]" />
                North Hillsborough
              </li>
              <li className="flex items-center gap-1">
                <CheckCircle2 className="h-3.5 w-3.5 text-[color:var(--chat-green)]" />
                North Pinellas
              </li>
            </ul>
          </div>

          {/* Business Hours */}
          <div data-testid="footer-business-hours">
            <h5 className="text-sm font-semibold uppercase tracking-wider mb-3">
              Hours
            </h5>
            <ul className="space-y-1 text-sm text-slate-300">
              <li>Mon - Fri: 9am - 5pm</li>
              <li>Saturday: 9am - 8pm</li>
              <li>Sunday: Emergency only</li>
            </ul>
          </div>

          {/* Contact Info */}
          <div data-testid="footer-contact">
            <h5 className="text-sm font-semibold uppercase tracking-wider mb-3">
              Business Info
            </h5>
            <p className="text-sm text-slate-300 leading-relaxed">
              Pasco Appliance LLC<br />
              Odessa, FL 33556<br />
              <a 
                href={`mailto:${EMAIL}`}
                className="underline decoration-white/30 underline-offset-4 hover:decoration-white transition-all"
              >
                {EMAIL}
              </a><br />
              <a 
                href="https://pascoappliance.com" 
                className="underline decoration-white/30 underline-offset-4 hover:decoration-white transition-all"
                target="_blank"
                rel="noopener noreferrer"
              >
                pascoappliance.com
              </a>
            </p>
          </div>
        </div>

        <Separator className="my-8 bg-white/10" />

        <div className="text-center text-sm text-slate-400 space-y-2">
          <p>© {new Date().getFullYear()} Pasco Appliance LLC. All rights reserved. Licensed & Insured.</p>
          
          <div className="flex justify-center gap-4">
            {/* Privacy Policy - Link */}
            <a 
              href="/privacy"
              className="text-slate-400 hover:text-white underline decoration-white/30 underline-offset-4 hover:decoration-white transition-all text-sm"
            >
              Privacy Policy
            </a>

            {/* Terms and Conditions - Link */}
            <a 
              href="/terms"
              className="text-slate-400 hover:text-white underline decoration-white/30 underline-offset-4 hover:decoration-white transition-all text-sm"
            >
              Terms & Conditions
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  return (
    <div className="App min-h-screen bg-white">
      <Header />
      <Hero />
      <Services />
      <About />
      <HowItWorks />
      <ContactForm />
      <PaymentMethods />
      <Footer />
    </div>
  );
}

export default App;
